import Cookies from 'js-cookie';
import { useCallback, useEffect, useMemo } from 'react';
import { createSelector } from 'reselect';

import { fillCondition } from '@laigu/utils';
import { useSelector, useDispatch } from '../../../react-redux';
import { updateAgentListAction, updateAgentListActionById, updateAgentMap } from '../../../reducers/agent';
import { getAgentListApi, changeAgentStatusApi, handleAgentOfflineApi } from './api';

const { isOnlineAgent } = fillCondition();
const empty = {};
const emptyList = [];

// 模块级标记，防止并发/重复请求
let hasFetched = false;

export const getRobotList = createSelector([agents => agents], agents => {
  return agents.filter(value => value.privilege === 'bot') || emptyList;
});

const getOnlineAgents = createSelector([agents => agents], agents => {
  return agents.filter(item => isOnlineAgent(item.serving_types)) || emptyList;
});

const useAgents = () => {
  const dispatch = useDispatch();
  const agentId = parseInt(Cookies.get('_agent_id'));

  //  在线客服
  const agentList = useSelector(state => getOnlineAgents(state.getIn(['agent', 'agentList'])));
  //  全部
  const allAgentList = useSelector(state => state.getIn(['agent', 'agentList']));
  const allAgentMap = useSelector(state => state.getIn(['agent', 'agentMap']));

  const me = useMemo(() => {
    return allAgentList.find(value => value.id === agentId) || empty;
  }, [allAgentList, agentId]);

  const robotAgent = useMemo(() => {
    return allAgentList.find(value => value.privilege === 'bot') || empty;
  }, [allAgentList]);

  const robotList = useSelector(state => getRobotList(state.getIn(['agent', 'agentList'])));

  const getAgentList = useCallback(async () => {
    const { data } = await getAgentListApi();
    if (data) {
      const arr = Object.values(data);
      const agentList = [];
      if (arr && Array.isArray(arr)) {
        arr.forEach(agent => {
          const newAgent = agent;
          if (newAgent.id === agentId) {
            newAgent.is_online = true;
          }
          if (agent.deleted_at) {
            newAgent.email = null;
          }
          agentList.push(newAgent);
        });
      }
      dispatch(updateAgentMap(data));
      dispatch(updateAgentListAction(() => agentList));
    }
  }, []);

  // 按需自动加载：首次有组件使用 useAgents 时触发，全局只请求一次
  useEffect(() => {
    if (hasFetched) return;
    hasFetched = true;
    getAgentList().catch(() => {
      hasFetched = false;
    });
  }, []);

  const updateAgentList = useCallback(func => {
    dispatch(updateAgentListAction(func));
  }, []);

  const updateAgentListById = useCallback((id, func) => {
    dispatch(updateAgentListActionById({ id, func }));
  }, []);

  const changeAgentStatus = useCallback(({ agentId, status }) => {
    return changeAgentStatusApi({ agentId, status });
  }, []);

  const handleAgentOffline = useCallback(
    agentId => {
      return handleAgentOfflineApi(agentId).then(() => {
        getAgentList();
      });
    },
    [getAgentList]
  );

  return {
    me,
    robotAgent,
    robotList,
    agentList,
    allAgentList,
    getAgentList,
    updateAgentList,
    updateAgentListById,
    changeAgentStatus,
    handleAgentOffline,
    allAgentMap
  };
};

export default useAgents;
